import { arrayToMap, createEnumHelper, omit, sortBy } from '@seiue/util'
import {
  AnswerReq,
  Assignment,
  Block,
  BlockTypeEnum,
  Question,
  QuestionTypeEnum,
} from 'packages/types'

import { AnswerData, HandoutTypeEnum, TransformedData } from './types'

/**
 * 格式化子题数据
 *
 * @param minorQuestions - 子题
 * @returns {formatAnalysisQuestions: Question[], formatQuestions: (Question &{ child:Question[]})[]} object
 */
export const formatMinorQuestions = (
  minorQuestions: Question[] | undefined
) => {
  const formatAnalysisQuestions: Question[] = []
  const formatQuestions: (Question & { children?: Question[] })[] = []
  minorQuestions?.forEach((item) => {
    if (item.type === QuestionTypeEnum.FillInBlank && !!item.difficultyLevel) {
      const currentFillMinor = minorQuestions.filter(
        (cur) => cur.parentId === item.id
      )

      formatAnalysisQuestions.push({
        ...item,
        standardAnswers: currentFillMinor.map(
          (cur) => cur.standardAnswers?.[0] || ''
        ),
      })

      formatQuestions.push({ ...item, children: currentFillMinor })
      return
    }

    if (item.type === QuestionTypeEnum.FillInBlank && !item.difficultyLevel) {
      return
    }

    formatQuestions.push(item)
    formatAnalysisQuestions.push(item)
  })

  return { formatAnalysisQuestions, formatQuestions }
}

/**
 * 将子题数据归类进 question.children
 *
 * @param questions - 题目数据
 * @returns {(Question & { children?: Question[] })[]} questions
 */
export const formatQuestionsWithChildren = (
  questions: Question[] | null | undefined
): (Question & { children?: Question[] })[] => {
  const mainQuestion = questions?.find((q) => q.parentId === 0)
  const minorQuestions = questions?.filter((q) => q.parentId !== 0)
  minorQuestions?.reverse()
  /**
   * 之前创建的数据没有 weight 是通过创建顺序倒序生产的
   * 现在新加的数据结构有 weight 字段，旧数据的 weight 为 0 无法更改，现在这里适配新数据的排序
   */
  const orderQuestions = sortBy(minorQuestions, 'weight')
  if (!mainQuestion) return []
  if (mainQuestion.type === QuestionTypeEnum.FillInBlank) {
    return [{ ...mainQuestion, children: orderQuestions }]
  }

  if (mainQuestion.type === QuestionTypeEnum.Product) {
    const { formatQuestions } = formatMinorQuestions(orderQuestions)
    return formatQuestions
  }

  return [mainQuestion]
}

/*
 * 批阅时-计算半对得分
 * 输入分数为 0: 回显 > 0.01
 * 输入分数为满分: 回显 > 满分 - 0.01
 * 输入分数为区间内值: 回显 > 用户输入值
 */
/**
 * 获取分数
 *
 * @param score - 分数
 * @param fullScore - 总分
 * @returns {number} score
 */
export const calHalfRightGainedScore = (
  score: number,
  fullScore: number
): number => {
  return score === 0 ? 0.01 : score >= fullScore ? fullScore - 0.01 : score
}

/**
 * 对下发的 Review, Answer, Stats 数组，根据 questionId 生成 { questionId: T } 对象
 * 再根据块的 blockId 生成 { blockId: { questionId: T } } 类型，方便根据 blockId 和 quetionId 定位存取
 *
 * @param blocks - 块数据
 * @param dataArray - 源数据
 * @param fallback - 如果 dataArray 中找不到对应 questionId 的数据，则使用默认 fallback，不传则默认为 undefined
 * @returns data
 */
export const formatQuestionData = <
  T extends {
    questionId?: number
  }
>(
  blocks: Block[],
  dataArray: T[],
  fallback?: Partial<T>
): TransformedData<T> => {
  // 筛选出 question 类型的 block
  const questionBlocks = blocks.filter(
    (block) => block.type === BlockTypeEnum.Question
  )

  // 生成 { questionId: T } 类型的 obj
  const dataObj = dataArray.reduce<{ [id: number]: T }>(
    (obj, data) => ({
      ...obj,
      [data.questionId as number]: data,
    }),
    {}
  )

  // 生成 { blockId: { questionId: T } } 类型的结果
  return questionBlocks.reduce(
    (blockData, block) => ({
      ...blockData,
      [block.id]: block.questions?.reduce(
        (questionData, ques) => ({
          ...questionData,
          [ques.id]: dataObj[ques.id] || {
            ...fallback,
            questionId: ques.id,
          },
        }),
        {}
      ),
    }),
    {}
  )
}

/**
 * 去除 answer 无效数据
 *
 * @param answerData - 答案数据
 * @returns AnswerReq[]
 */
export const flattenAnswer: (answerData: AnswerData) => AnswerReq[] = (
  answerData
) => {
  const result: AnswerReq[] = []
  const blockIds = Object.keys(answerData)
  blockIds.forEach((blockId) => {
    const idAnswerPair = answerData[Number(blockId)]
    const questionIds = Object.keys(idAnswerPair)
    questionIds.forEach((questionId) => {
      const answer = idAnswerPair[Number(questionId)]
      if (answer) {
        result.push(answer.id === 0 ? omit(answer, ['id']) : answer)
      }
    })
  })

  return result
}

/**
 * 将 assignments 数据转化为 { [outlineId: number]: Assignment } 结构
 * 方便数据下发
 *
 * @param assignments - assignments
 * @returns 格式化后的数据
 */
export const formatAssignment = (assignments: Assignment[] | null) =>
  (assignments || []).reduce<{ [outlineId: number]: Assignment }>(
    (prev, asmt) => ({
      ...prev,
      [asmt.outlineId]: asmt,
    }),
    {}
  )

/**
 * 计算章节总分
 *
 * @param questionBlocks - 所有的题目块
 * @returns 章节总分
 */
export const computeQuestionBlocksTotalScore = (
  questionBlocks: Block[] | null | undefined
) => {
  if (!questionBlocks) return 0

  return Number(
    questionBlocks
      .filter((b) => b.type === BlockTypeEnum.Question)
      .map((block) => {
        const questionGroup = formatQuestionsWithChildren(block.questions)

        if (questionGroup.length === 1) {
          // 简单题型
          return Number(questionGroup.at(0)?.score || 0)
        }

        // 复合题型
        return questionGroup.reduce(
          (sum, ques) => sum + Number(ques.score || 0),
          0
        )
      })
      .reduce((sum, cur) => sum + cur, 0)
      .toFixed(2)
  )
}

/**
 * 学案类型转为 tab 标签
 */
export const handoutTypeToTabLabel = createEnumHelper(() => ({
  [HandoutTypeEnum.RootHandout]: '学案库',
  [HandoutTypeEnum.CourseHandout]: '课程库',
  [HandoutTypeEnum.ClassHandout]: '课程班',
  [HandoutTypeEnum.TaskRelatedHandout]: '测验',
  [HandoutTypeEnum.QuestionPool]: '题库',
})).get

/**
 * 对比修改过后的块数据，返回是否有非分数字段发生了变化
 *
 * @param prevBlock - 修改前的块数据
 * @param block - 修改后的块数据
 * @returns boolean
 */
export const hasNonScoreFieldChanged = (prevBlock: Block, block: Block) => {
  // 如果问题数量不一致，返回 true
  if (prevBlock.questions?.length !== block.questions?.length) return true
  const preQuestionIds = prevBlock.questions?.map((q) => q.id)
  const questionIds = block.questions?.map((q) => q.id)

  // 如果问题 id 前后有删改，返回 true
  if (questionIds?.some((id) => !preQuestionIds?.includes(id))) return true

  const prevQuestionIdBlock = arrayToMap(prevBlock.questions ?? [], 'id')
  const questionIdBlock = arrayToMap(block.questions ?? [], 'id')

  // 如果有问题的非 score 字段发生了变化，返回 true
  return block.questions?.some((question) =>
    (Object.keys(question) as (keyof Question)[]).some(
      (key) =>
        ![
          'score',
          'treeNodeId',
          'treeNodePid',
          'startSetting',
          'queSources',
          'validFileUploadingMap',
        ].includes(key) &&
        prevQuestionIdBlock[question.id][key]?.toString() !==
          questionIdBlock[question.id][key]?.toString()
    )
  )
}
